import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useHistoryRestoreGuard, useHistoryState } from '../hooks/useViewInHistory';
import { apiGet, apiPost, apiPut, apiPatch, apiDelete, apiThrow } from '../utils/api';
import { useDepartment } from '../contexts/DepartmentContext';
import { useToast } from '../contexts/ToastContext';
import { usePageTitle, usePageSummary } from '../contexts/PageHeaderContext';
import useStickyToolbar from '../hooks/useStickyToolbar';
import { scrollUnderPinnedChrome } from '../hooks/useOpenAtTop';
import useSubmitGuard from '../hooks/useSubmitGuard';
import useUnsavedChangesGuard from '../hooks/useUnsavedChangesGuard';
import useDirtyPrompt from '../hooks/useDirtyPrompt';
import { UNSAVED_PROMPT_BUTTONS } from '../constants/unsavedChangesPrompt';
import { useIsMobileOrderViewport } from '../hooks/useIsMobileOrderViewport';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorMessage from '../components/ErrorMessage';
import EmptyState from '../components/EmptyState';
import ConfirmDialog from '../components/ConfirmDialog';
import UserAutocomplete from '../components/UserAutocomplete';
import Toolbar, { TOOLBAR_DISCLOSURE_TRIGGER, TOOLBAR_RIGHT_SLOT } from '../components/ui/Toolbar';
import Tabs from '../components/ui/Tabs';
import SearchInput from '../components/ui/SearchInput';
import Dropdown from '../components/ui/Dropdown';
import GroupControl from '../components/ui/GroupControl';
import FilterChipMenu from '../components/ui/FilterChipMenu';
import PageScrollTable from '../components/ui/PageScrollTable';
import { useGroupedList, GroupHeader } from '../components/ui/GroupedList';
import { MENU_ITEM_NEUTRAL } from '../components/OrderActionsMenu';
import MobileTabSearchRow, { PHONE_HEADER_TOOLBAR } from '../components/MobileTabSearchRow';
import PhoneAddButton from '../components/ui/PhoneAddButton';
import { PHONE_SECTION_BODY, PHONE_FLOAT_ITEM, PHONE_GROUP_WRAPPER, PHONE_GROUP_HEADER } from '../constants/phoneLayout';
import { statusPillClass } from '../components/ui/StatusPill';
import TaskTable, { taskTableColumns, sortTasks, nextTaskSort } from '../components/tasks/TaskTable';
import TaskEditor from '../components/teams/TaskEditor';
import TaskKanbanView from '../components/teams/TaskKanbanView';
import CrossTeamGanttView from '../components/teams/CrossTeamGanttView';
import { BTN, BTN_XS, TAP_TARGET_PHONE } from '../constants/buttons';
import { TASK_STATUS_TAB_ALL, TASK_STATUS_TABS, tasksInStatusTab, taskStatusTabCounts, taskStatusTabOf } from '../constants/taskStatuses';
import { GAG_STATUS_COLORS } from '../constants/colors';
import { GAG_STATUS_LABELS } from '../constants/taskStatuses';
import { FIELD_LABEL_CLASS } from '../constants/inputs';
import { DUE_BUCKETS, dueBucket, shootDayBucket } from '../utils/dueStatus';
import { formatDateCompact, formatDateDayMonth, toLocalISO } from '../utils/dateFormat';
import { LIST_PAGE_SHELL } from '../constants/pageLayout';

/**
 * MY TASKS — Gags/teams/tasks Stage 1 (owner sign-off 2026-10-02,
 * `docs/superpowers/specs/2026-10-02-gags-teams-tasks-simplification-design.md`
 * "My Tasks page"). Replaced `pages/MyTeamTasks.js` (pre-change SHA 2b2aafc7).
 *
 * ONE request (`GET /api/tasks/my-work`, the department you stand in) feeds
 * three tabs:
 *   My work     — every gag I OWN or am on the TEAM of (all its tasks: mine in
 *                 full, everyone else's FADED), then no-gag tasks grouped by who
 *                 gave them to me ("From <name>", keyed on the ID), then
 *                 "Personal tasks" (ones I gave myself) LAST;
 *   Given by me — every task I gave to someone else (shown only once I have);
 *   Unassigned  — unowned gags (breakdown-rights holders only — the server
 *                 sends it empty to everyone else).
 * 🔀 HEADER ROUND (owner, 2026-10-02): the status TAB ROW is now ONE status
 * chip (`ui/FilterChipMenu`: `All ▾` → `● Blocked ×`, counts in its menu,
 * from the same predicate as the rows), and `Group ▾` is `View by ▾`
 * (`ui/GroupControl` with `renderLabel`): Gag · Shoot date · Due date · Status
 * · Person · Sub-department, plus None (flat list) on DESKTOP only. The same
 * control on every tab, SESSION state, defaulting per tab (My work → Gag,
 * Given by me → Person). The swipe-between-statuses gesture went with the tab
 * row. Search covers task, gag and person names and opens the groups it matches.
 *
 * 🔑 THIS PAGE DECIDES NOTHING ABOUT AUTHORITY. Every write is re-checked by
 * the server (`server/helpers/gagTeamAccess.js`); the client only chooses what
 * to OFFER, from what the payload says (`relation`, `can_assign_others`).
 * 📱 Phone: TWO rows only — the tabs, then (pinned) [🔍] [All ▾] [View: Gag ▾]
 * [⋯], the ⋯ bottom sheet holding Expand all, Open Breakdown and + New task.
 * Table only on a phone. 🖥️ Desktop (owner, 2026-10-12: match the Breakdown):
 * ONE pinned bar of two rows — search · Table | Kanban | Timeline · status
 * chip · View by · Actions ▾ · + New task, then the page tabs beneath, as the
 * Breakdown's status strip sits beneath its controls.
 */

export const MY_TASKS_TABS = [
  { id: 'mine', label: 'My work' },
  { id: 'given', label: 'Delegated' }, // was "Given by me" (owner 2026-10-03)
  { id: 'unassigned', label: 'Unassigned' },
];
/** View by ▾ — the owner's order (2026-10-02). `desktopOnly`: a phone has no
 *  flat list (it has no column headers to sort one by). */
export const VIEW_BY_OPTIONS = [
  { id: 'gag', label: 'Gag' },
  { id: 'shoot', label: 'Shoot date' },
  { id: 'due', label: 'Due date' },
  { id: 'status', label: 'Status' },
  { id: 'person', label: 'Person' },
  { id: 'subdept', label: 'Sub-department' },
  { id: 'none', label: 'None (flat list)', desktopOnly: true },
];
/** The phone chip's short value (the menu keeps the full names). */
const PHONE_VIEW_LABEL = { shoot: 'Shoot', due: 'Due', subdept: 'Sub-dept' };
export const viewByOptions =(isMobile) => VIEW_BY_OPTIONS.filter((o) => !(isMobile && o.desktopOnly));
export const DEFAULT_GROUP = { mine: 'gag', given: 'person', unassigned: 'subdept' };
// 📱 Phone defaults (owner, 2026-10-02): the status chip is gone on a phone and
// the default View is Status — collapsed To do / In progress / Blocked / Done
// sections. View ▾ still offers Gag (a gag's whole task list in one section).
export const PHONE_DEFAULT_GROUP = { mine: 'status', given: 'status', unassigned: 'subdept' };
export const defaultGroupFor = (tab, isMobile) => (isMobile ? PHONE_DEFAULT_GROUP : DEFAULT_GROUP)[tab];
/** 📱 The Unassigned tab's View ▾ on a phone (owner, 2026-10-03: *"so we can
 *  still search the unassigned and arrange"*). Desktop keeps Sub-department. */
export const UNASSIGNED_VIEW_OPTIONS = [
  { id: 'subdept', label: 'Sub-department' },
  { id: 'shoot', label: 'Shoot date' },
  { id: 'none', label: 'Flat' },
];
const shootKey = (g) => (g.shoot_date ? String(g.shoot_date).split('T')[0] : '');
/** Shoot date ASC, NO date FIRST (the breakdown's flat-list ruling). */
const byShootNullsFirst = (a, b) => shootKey(a).localeCompare(shootKey(b));
/**
 * The Unassigned gags as groups. Pure. `subdept` = today's grouping (server
 * order); `shoot` = `shootDayBucket` days (My Tasks' own Shoot-date view);
 * `none` = ONE unheaded group (`label: null`), shoot date ASC NULLS FIRST.
 */
export function groupUnassignedGags(gags, groupBy, today = new Date()) {
  if (groupBy === 'none') return gags.length ? [{ key: 'u:all', label: null, gags: [...gags].sort(byShootNullsFirst) }] : [];
  const by = new Map();
  for (const g of gags) {
    let k; let label; let rank = 0;
    if (groupBy === 'shoot') {
      const b = shootDayBucket(g.shoot_date, today);
      k = b.key; label = b.label; rank = b.rank;
    } else {
      k = g.sub_department_id == null ? 'none' : String(g.sub_department_id);
      label = g.sub_department_name || 'No sub-department';
    }
    if (!by.has(k)) by.set(k, { key: `u:${groupBy}:${k}`, label, rank, day: k, gags: [] });
    by.get(k).gags.push(g);
  }
  const groups = [...by.values()];
  return groupBy === 'shoot' ? groups.sort((a, b) => a.rank - b.rank || a.day.localeCompare(b.day)) : groups;
}
const VIEWS = ['list', 'kanban', 'gantt'];
/** The view tabs, named as the Breakdown names its own (owner, 2026-10-12).
 *  The ids are unchanged: `sfx_my_tasks_view` already stores them. */
export const MY_TASKS_VIEW_TABS = [
  { id: 'list', label: 'Table' },
  { id: 'kanban', label: 'Kanban' },
  { id: 'gantt', label: 'Timeline' },
];
const VIEW_KEY = 'sfx_my_tasks_view';
const PERSONAL_KEY = 'personal';

const readView = () => {
  try {
    const v = window.localStorage.getItem(VIEW_KEY);
    return VIEWS.includes(v) ? v : 'list';
  } catch { return 'list'; }
};

/** Case-insensitive substring over the task's own, gag, assigner and assignee names. */
export function myTaskMatchesSearch(task, term) {
  const t = String(term || '').trim().toLowerCase();
  if (!t) return true;
  return [task.title, task.gag_title, task.assigned_by_name, task.created_by_name,
    ...(task.assignees || []).map((a) => a.display_name)]
    .some((v) => v != null && String(v).toLowerCase().includes(t));
}

/** A task is "mine" when it is assigned to me, or nobody holds it and I made it. */
export const isMine = (task, me) => !!task.assigned_to_me
  || ((task.assignees || []).length === 0 && Number(task.created_by) === Number(me));

/**
 * The "From <assigner>" key of a no-gag task on My work: the assigner's ID
 * (never the name — two people can share one), or PERSONAL when I gave it to
 * myself / nobody gave it to anyone.
 */
export const fromKeyOf = (task) => (task.assigned_by != null ? `from:${task.assigned_by}` : PERSONAL_KEY);

/**
 * THE rows a My Tasks view shows. 🔀 Owner ruling 2026-10-03 (option A): on My
 * work EVERY view except Gag shows only the viewer's own tasks; the Gag view
 * alone shows the whole gag with other people's tasks faded. ONE rule, read by
 * `buildGroups` (the list) AND the status chip's counts, so the two cannot
 * disagree (they did: a delegated task was counted but not shown).
 */
export const viewRows = (rows, tab, groupBy, me) => (tab === 'mine' && groupBy !== 'gag' ? rows.filter((t) => isMine(t, me)) : rows);

/**
 * Build the visible groups for a tab + grouping. Pure. Returns
 * [{ key, label, kind, tasks, gag?, personId?, emptyNote? }] in display order;
 * a single `{ label: null }` group is a flat list.
 */
export function buildGroups({ tab, groupBy, rows: allRows, gags, me, todayDate = new Date() }) {
  const rows = viewRows(allRows, tab, groupBy, me); // (Due already did this.)
  if (groupBy === 'none') return [{ key: 'flat', label: null, kind: 'flat', tasks: rows }];

  if (groupBy === 'gag') {
    const groups = [];
    const byGag = new Map();
    if (tab === 'mine') {
      for (const g of gags) {
        const grp = { key: `gag:${g.id}`, label: g.title, kind: 'gag', gag: g, tasks: [] };
        byGag.set(Number(g.id), grp);
        groups.push(grp);
      }
    }
    const fromGroups = new Map();
    let personal = null;
    let noGag = null;
    for (const t of rows) {
      if (t.gag_id != null) {
        let grp = byGag.get(Number(t.gag_id));
        if (!grp) {
          grp = { key: `gag:${t.gag_id}`, label: t.gag_title || `Gag ${t.gag_id}`, kind: 'gag', gag: { id: t.gag_id, title: t.gag_title }, tasks: [] };
          byGag.set(Number(t.gag_id), grp);
          groups.push(grp);
        }
        grp.tasks.push(t);
      } else if (tab === 'mine') {
        const k = fromKeyOf(t);
        if (k === PERSONAL_KEY) {
          if (!personal) personal = { key: PERSONAL_KEY, label: 'Personal tasks', kind: 'personal', tasks: [] };
          personal.tasks.push(t);
        } else {
          if (!fromGroups.has(k)) fromGroups.set(k, { key: k, label: `From ${t.assigned_by_name || 'someone'}`, kind: 'from', personId: t.assigned_by, tasks: [] });
          fromGroups.get(k).tasks.push(t);
        }
      } else {
        if (!noGag) noGag = { key: 'nogag', label: 'No gag', kind: 'nogag', tasks: [] };
        noGag.tasks.push(t);
      }
    }
    // A gag group with no rows survives only on My work, only for a gag I OWN
    // ("a gag I own with 0 tasks still shows, with + Add task") — and only when
    // nothing narrows the list (the caller passes the unfiltered gag list then).
    const gagGroups = groups.filter((g) => g.tasks.length > 0 || (tab === 'mine' && g.gag && g.gag.relation === 'owner'));
    for (const g of gagGroups) if (g.tasks.length === 0) g.emptyNote = 'No tasks yet.';
    // "From" headers: a NAME CLASH between two different people adds a
    // disambiguator (spec: "a header clash adds their role" — the payload has
    // no role, so the person's id is shown; the GROUPING is by id either way).
    const froms = [...fromGroups.values()].sort((a, b) => a.label.localeCompare(b.label));
    const seen = new Map();
    for (const f of froms) seen.set(f.label, (seen.get(f.label) || 0) + 1);
    for (const f of froms) if (seen.get(f.label) > 1) f.label = `${f.label} (#${f.personId})`;
    return [...gagGroups, ...froms, ...(noGag ? [noGag] : []), ...(personal ? [personal] : [])];
  }

  if (groupBy === 'person') {
    const byPerson = new Map();
    const nobody = { key: 'person:none', label: 'Unassigned', kind: 'person', tasks: [] };
    for (const t of rows) {
      const people = tab === 'given'
        ? (t.assignees || []).filter((a) => Number(a.assigned_by) === Number(me) && Number(a.user_id) !== Number(me))
        : (t.assignees || []);
      if (people.length === 0) { nobody.tasks.push(t); continue; }
      for (const p of people) {
        const k = `person:${p.user_id}`;
        if (!byPerson.has(k)) byPerson.set(k, { key: k, label: p.display_name, kind: 'person', personId: p.user_id, tasks: [] });
        byPerson.get(k).tasks.push(t);
      }
    }
    const groups = [...byPerson.values()].sort((a, b) => a.label.localeCompare(b.label));
    if (nobody.tasks.length) groups.push(nobody);
    return groups;
  }

  if (groupBy === 'due') {
    // On My work the Due grouping is MY tasks only (spec); each row names its gag.
    const pool = tab === 'mine' ? rows.filter((t) => isMine(t, me)) : rows;
    const byBucket = new Map(DUE_BUCKETS.map((b) => [b.id, { key: `due:${b.id}`, label: b.label, kind: 'due', tasks: [] }]));
    for (const t of pool) byBucket.get(dueBucket(t.due_date, t.status, todayDate)).tasks.push(t);
    return [...byBucket.values()].filter((g) => g.tasks.length > 0);
  }

  if (groupBy === 'shoot') {
    // The GAG's shoot day (a task row keeps its own due date); no-gag tasks
    // under "No shoot date", past days together under "Shot", both last.
    const byDay = new Map();
    for (const t of rows) {
      const b = shootDayBucket(t.gag_id != null ? t.gag_shoot_date : null, todayDate);
      if (!byDay.has(b.key)) byDay.set(b.key, { key: `shoot:${b.key}`, label: b.label, kind: 'shoot', rank: b.rank, day: b.key, tasks: [] });
      byDay.get(b.key).tasks.push(t);
    }
    return [...byDay.values()].sort((a, b) => a.rank - b.rank || a.day.localeCompare(b.day));
  }

  if (groupBy === 'status') {
    // To do · In progress · Blocked · Done (holding cancelled) — the status
    // chip's own predicate, so the chip then filters WITHIN these sections.
    const sections = TASK_STATUS_TABS.filter((s) => s.id !== TASK_STATUS_TAB_ALL)
      .map((s) => ({ key: `status:${s.id}`, label: s.label, kind: 'status', tasks: [] }));
    const byId = new Map(sections.map((g) => [g.key, g]));
    for (const t of rows) {
      const g = byId.get(`status:${taskStatusTabOf(t.status)}`);
      if (g) g.tasks.push(t);
    }
    return sections.filter((g) => g.tasks.length > 0);
  }

  // Sub-department: gags grouped under their sub-department; no-gag tasks last.
  const bySub = new Map();
  const noSub = { key: 'sub:none', label: 'No sub-department', kind: 'subdept', tasks: [] };
  const noGag = { key: 'sub:nogag', label: 'No gag', kind: 'subdept', tasks: [] };
  for (const t of rows) {
    if (t.gag_id == null) { noGag.tasks.push(t); continue; }
    if (t.gag_sub_department_id == null) { noSub.tasks.push(t); continue; }
    const k = `sub:${t.gag_sub_department_id}`;
    if (!bySub.has(k)) bySub.set(k, { key: k, label: t.gag_sub_department_name || 'Sub-department', kind: 'subdept', tasks: [] });
    bySub.get(k).tasks.push(t);
  }
  return [...[...bySub.values()].sort((a, b) => a.label.localeCompare(b.label)), noSub, noGag].filter((g) => g.tasks.length > 0);
}

/** The words after a group's name: "Owner · 8 tasks · 2 for you · 1 overdue · next due 14 Oct"
 *  ("for you", not "yours" — owner wording, 2026-10-02 fix round). */
function GroupSummary({ group, me, todayStr, tab }) {
  const n = group.tasks.length;
  const open = (t) => t.status !== 'done' && t.status !== 'cancelled';
  const overdue = group.tasks.filter((t) => open(t) && t.due_date && t.due_date < todayStr).length;
  const yours = group.tasks.filter((t) => isMine(t, me)).length;
  const next = group.tasks.filter((t) => open(t) && t.due_date && t.due_date >= todayStr).map((t) => t.due_date).sort()[0];
  // 🔀 Owner 2026-10-03: urgent facts FIRST so a cut line ("…") loses the
  // least useful part: overdue · due <date> · N of M for you · role.
  const parts = [];
  if (overdue > 0) parts.push(<span key="o" className="font-semibold text-red-700 dark:text-red-300">{overdue} overdue</span>);
  if (next && group.kind !== 'due') parts.push(<span key="d">due {formatDateDayMonth(next)}</span>);
  if (group.kind === 'gag' && tab === 'mine' && yours > 0) parts.push(<span key="y">{yours} of {n} for you</span>);
  else parts.push(<span key="n">{n} {n === 1 ? 'task' : 'tasks'}</span>);
  if (group.kind === 'gag' && group.gag && group.gag.relation) {
    parts.push(<span key="rel">{group.gag.relation === 'owner' ? 'Owner' : group.gag.relation === 'team' ? 'Team' : 'Assigned'}</span>);
  }
  return (
    <span className="text-xs font-normal text-gray-600 dark:text-gray-300" data-testid={`my-tasks-group-summary-${group.key}`}>
      {parts.flatMap((p, i) => (i === 0 ? [p] : [<span key={`s${i}`} aria-hidden="true"> · </span>, p]))}
    </span>
  );
}

export default function MyTasks() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { activeDepartment } = useDepartment();
  const deptId = activeDepartment ? activeDepartment.department_id : null;
  const isMobile = useIsMobileOrderViewport();
  const { toolbarRef, headerTop } = useStickyToolbar();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deptUsers, setDeptUsers] = useState([]);
  const [subDepts, setSubDepts] = useState([]);

  // 🔀 2026-10-13: the tab and the status tab below live in the history entry
  // (`hooks/useViewInHistory`): a browser Back returns to them. Not stored; a
  // fresh visit opens on My work / All as before.
  const [tab, setTab] = useHistoryState('my-tasks-tab', 'mine', (v) => MY_TASKS_TABS.some((t) => t.id === v));
  // null = the tab's default for this width (defaultGroupFor).
  const [groupChoice, setGroupBy] = useState(null);
  const [statusTab, setStatusTab] = useHistoryState('my-tasks-status', TASK_STATUS_TAB_ALL, (v) => TASK_STATUS_TABS.some((t) => t.id === v));
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState({ field: 'due_date', dir: 'asc' });
  const [view, setView] = useState(readView);
  const [expandedId, setExpandedId] = useState(null);
  // A task to SHOW in the Table: its group opened and the row scrolled under
  // the pinned bar (a Kanban/Timeline click, a ?task= link). One-shot.
  const [revealId, setRevealId] = useState(null);
  const [editorDirty, setEditorDirty] = useState(false);
  const [newTask, setNewTask] = useState(null); // { gagId? } while the dialog is open

  const leaveGuard = useUnsavedChangesGuard({
    when: editorDirty,
    message: "Your changes to this task haven't been saved. If you leave, they will be lost.",
  });

  // 🔀 ASK-THEN-CONTINUE (owner 2026-10-07, "A, convert all three"): while the
  // inline task editor is dirty, every in-page exit — a tab, View by, the status
  // chip, another row, the view switch, Open gag / Open Breakdown, Collapse all,
  // + New task / + Add task — raises the three-way prompt and then runs the
  // click on Save (ok) or Discard, never on Keep editing or a failed save
  // (`hooks/useDirtyPrompt.js`). It used to REFUSE and pulse the editor
  // (`editorWarn`, deleted; pre-deletion SHA 5b4ed62c). Search never asks.
  // `saveRef` / `discardRef` take the open editor's own save / discard
  // (TaskEditor's `save` / `revert`); `hooks/useDirtyPrompt.js` does the rest.
  const {
    continueWhenClean, saveRef: editorSaveRef, discardRef: editorDiscardRef, promptProps,
  } = useDirtyPrompt({ dirty: editorDirty });
  // A Back/Forward that stays on this page (a bell link pushes `?task=` here)
  // asks too: see `useHistoryRestoreGuard`.
  useHistoryRestoreGuard(editorDirty, continueWhenClean);

  const fetchData = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const res = await apiGet('/api/tasks/my-work');
      if (!res.ok) throw new Error('Failed to load tasks');
      setData(await res.json());
      setError(null);
    } catch {
      if (!silent) setError('Failed to load your tasks');
      else toast.error('Could not refresh your tasks — showing the last loaded list.');
    }
    setLoading(false);
  }, [toast]);

  useEffect(() => { fetchData(); }, [fetchData, deptId]);

  // The assignee picker + sub-department field, for the department stood in.
  useEffect(() => {
    if (!deptId) return;
    (async () => {
      try {
        const [uRes, sRes] = await Promise.all([
          apiGet(`/api/gags/assignable-users?department_id=${deptId}`),
          apiGet(`/api/sub-departments?department_id=${deptId}`),
        ]);
        if (uRes.ok) setDeptUsers(await uRes.json());
        if (sRes.ok) {
          const s = await sRes.json();
          setSubDepts(Array.isArray(s) ? s : []);
        }
      } catch { /* the editor degrades to an empty picker; the server is the gate */ }
    })();
  }, [deptId]);

  const me = data ? data.me : null;
  const canAssignOthers = !!(data && data.can_assign_others);
  const gags = useMemo(() => (data ? data.gags : []), [data]);
  const gagById = useMemo(() => new Map(gags.map((g) => [Number(g.id), g])), [gags]);
  const allTasks = useMemo(() => (data ? data.tasks : []), [data]);
  const todayStr = toLocalISO(new Date());

  // ── The tab row sets ──
  const mineRows = useMemo(() => allTasks.filter((t) => (t.gag_id != null
    ? gagById.has(Number(t.gag_id))
    : isMine(t, me))), [allTasks, gagById, me]);
  const givenRows = useMemo(() => allTasks.filter((t) => t.given_by_me), [allTasks]);
  const unassigned = data ? data.unassigned : [];

  const tabs = MY_TASKS_TABS.filter((t) => t.id === 'mine'
    || (t.id === 'given' && givenRows.length > 0)
    || (t.id === 'unassigned' && canAssignOthers))
    .map((t) => ({ ...t, count: t.id === 'mine' ? mineRows.filter((r) => isMine(r, me)).length /* owner 2026-10-03: your own tasks only */ : t.id === 'given' ? givenRows.length : unassigned.length }));
  const activeTab = tabs.some((t) => t.id === tab) ? tab : 'mine';
  const isGagTab = activeTab === 'unassigned';
  // A phone offers no flat list; a desktop-only choice carried across a
  // resize falls back to the tab's default rather than to nothing.
  const groupBy = viewByOptions(isMobile).some((o) => o.id === groupChoice) ? groupChoice : defaultGroupFor(activeTab, isMobile);
  // Unassigned: View ▾ is phone-only, so desktop always reads Sub-department.
  const unassignedView = isMobile && UNASSIGNED_VIEW_OPTIONS.some((o) => o.id === groupChoice) ? groupChoice : 'subdept';

  const term = search.trim();
  const tabRows = activeTab === 'given' ? givenRows : mineRows;
  const searched = term ? tabRows.filter((t) => myTaskMatchesSearch(t, term)) : tabRows;
  const statusCounts = taskStatusTabCounts(viewRows(searched, activeTab, groupBy, me));
  const statusOptions = TASK_STATUS_TABS.map((t) => ({ ...t, count: statusCounts[t.id] ?? 0 }));
  const statusShown = isGagTab || view !== 'kanban' || isMobile;
  // 📱 No status filter on a phone (the chip is desktop-only) — sections do the job.
  const visible = statusShown && !isMobile ? tasksInStatusTab(searched, statusTab) : searched;
  // The faded rule: on My work's Gag grouping, everyone else's task is muted.
  const fadeOthers = activeTab === 'mine' && groupBy === 'gag';

  // Gag groups that are EMPTY (an owned gag with no tasks) only make sense on
  // the unnarrowed list — a search or a status filter hides them.
  const gagsForGroups = term || statusTab !== TASK_STATUS_TAB_ALL ? [] : gags;
  const groups = useMemo(() => {
    const built = buildGroups({ tab: activeTab, groupBy, rows: visible, gags: gagsForGroups, me });
    const contextText = (t) => (t.gag_title || '');
    return built.map((g) => ({ ...g, tasks: groupBy === 'none' ? sortTasks(g.tasks, sort.field, sort.dir, contextText) : sortTasks(g.tasks, 'due_date', 'asc') }));
  }, [activeTab, groupBy, visible, gagsForGroups, me, sort]);

  // 🔀 Owner 2026-10-03: "we can't collapse a group like To do if a task within
  // it is expanded". A group is held open only while its expanded task has
  // UNSAVED edits (collapsing would hide them); a clean expanded task no longer
  // pins its group — it stays expanded inside, and shows again on reopen.
  const holdsExpanded = useCallback(
    (g) => editorDirty && g.tasks.some((t) => t.id === expandedId),
    [expandedId, editorDirty]
  );
  const titled = groups.filter((g) => g.label != null);
  const groupState = useGroupedList({ defaultOpen: !!term || titled.length === 1, forceOpen: holdsExpanded });

  // ── Exits ASK while an inline edit is unsaved (see `continueWhenClean`
  // above). `ask(fn)` runs `fn` now when clean; when dirty it holds a closure
  // that re-enters the LATEST `fn` through `latest` (the hook's point 2). ──
  const latest = useRef({});
  const ask = (name, ...args) => continueWhenClean(() => latest.current[name](...args));
  const doChangeTab = (id) => {
    setTab(id);
    setGroupBy(null);
    setStatusTab(TASK_STATUS_TAB_ALL);
    setExpandedId(null);
    groupState.reset();
  };
  const doChangeGroup = (id) => { setGroupBy(id); groupState.reset(); };
  const doToggleRow = (task) => setExpandedId((cur) => (cur === task.id ? null : task.id));
  const doChangeView = (v) => {
    setView(v);
    try { window.localStorage.setItem(VIEW_KEY, v); } catch { /* per-viewer convenience only */ }
  };
  const changeTab = (id) => { if (id !== activeTab) ask('doChangeTab', id); };
  const changeGroup = (id) => ask('doChangeGroup', id);
  const changeStatus = (id) => { if (id !== statusTab) ask('setStatusTab', id); };
  const changeSearch = (v) => { setSearch(v); groupState.reset(); };
  const toggleRow = (task) => ask('doToggleRow', task);
  const changeView = (v) => { if (VIEWS.includes(v) && v !== view) ask('doChangeView', v); };
  // Kanban / Timeline click → that task, open, in the Table (owner 2026-10-12:
  // "it should expand the task that was clicked on" — it was expanded inside a
  // group that stayed collapsed). A status chip that would hide it is cleared:
  // Kanban shows every status, so the click can land on a filtered-out task.
  const doOpenInTable = (task) => {
    doChangeView('list');
    if (statusTab !== TASK_STATUS_TAB_ALL && taskStatusTabOf(task.status) !== statusTab) setStatusTab(TASK_STATUS_TAB_ALL);
    setExpandedId(task.id);
    setRevealId(task.id);
  };
  const openInTable = (task) => ask('doOpenInTable', task);
  // A phone has no Kanban/Gantt (List only).
  const effectiveView = isMobile ? 'list' : view;

  // ── Deep link ?task=<id> — land on the tab that holds it, on All, expanded.
  const deepLinked = useRef(null);
  useEffect(() => {
    if (!data) return;
    const id = parseInt(searchParams.get('task'), 10);
    if (!id || deepLinked.current === id) return;
    deepLinked.current = id;
    const t = allTasks.find((x) => x.id === id);
    if (!t) return;
    const inMine = mineRows.some((x) => x.id === id);
    setTab(inMine ? 'mine' : 'given');
    setGroupBy(null);
    setStatusTab(TASK_STATUS_TAB_ALL);
    // The link must SHOW the task, so it lands on the Table whatever view is
    // stored — and never rewrites that stored choice (state only).
    setView('list');
    setExpandedId(id);
    setRevealId(id);
  }, [data, searchParams, allTasks, mineRows]);

  // Reveal: open the group holding the task (an explicit toggle, so the user
  // can close it again), then scroll its row under the pinned chrome
  // (`scrollUnderPinnedChrome`, the house jump-to-row). Runs until done, then clears.
  useEffect(() => {
    if (revealId == null || effectiveView !== 'list') return;
    const g = groups.find((x) => x.tasks.some((t) => t.id === revealId));
    if (g && g.label != null && !groupState.isOpen(g)) { groupState.setAll([g], true); return; }
    if (g) scrollUnderPinnedChrome(document.querySelector(`[data-testid="my-task-row-${revealId}"]`));
    setRevealId(null);
  }, [revealId, effectiveView, groups, groupState]);

  // The held navigate runs once clean, so the leave guard is already disarmed.
  const openGag = (gagId) => ask('navigate', `/teams/breakdown?gag=${gagId}`);
  const openNewTask = (init) => ask('setNewTask', init);
  latest.current = {
    doChangeTab, doChangeGroup, doToggleRow, doChangeView, doOpenInTable, setStatusTab, navigate, setNewTask,
  };

  // ── Rights the page OFFERS (the server re-checks every write). ──
  const gagRelation = (t) => (t.gag_id != null ? (gagById.get(Number(t.gag_id)) || {}).relation : null);
  const canEditTask = (t) => (t.gag_id == null
    ? true // a no-gag task in this payload is mine to see, so mine to edit (creator + assignees)
    : canAssignOthers || ['owner', 'team'].includes(gagRelation(t)) || Number(t.created_by) === Number(me));
  const canAssignTask = (t) => (t.gag_id == null ? true : canAssignOthers || gagRelation(t) === 'owner');
  const pickerOptions = (t) => {
    if (t.gag_id == null) {
      // Rule 5: breakdown rights may give a no-gag task to anyone here;
      // everyone else only to themselves.
      return canAssignOthers ? deptUsers.map((u) => ({ ...u })) : deptUsers.filter((u) => Number(u.id) === Number(me));
    }
    const g = gagById.get(Number(t.gag_id));
    const team = new Set([g && g.owner_id, ...((g && g.team_user_ids) || [])].filter((x) => x != null).map(Number));
    return [
      ...deptUsers.filter((u) => team.has(Number(u.id))).map((u) => ({ ...u, group: 'Team' })),
      ...deptUsers.filter((u) => !team.has(Number(u.id))).map((u) => ({ ...u, group: 'Department' })),
    ];
  };
  const taskUrl = (t) => (t.gag_id != null ? `/api/gags/${t.gag_id}/tasks/${t.id}` : `/api/tasks/${t.id}`);

  const renderExpanded = (t) => (
    <TaskEditor
      key={t.id}
      task={t}
      canEdit={canEditTask(t)}
      canAssign={canAssignTask(t)}
      assignPicker={{ options: pickerOptions(t) }}
      readOnlyNote=" — only the gag owner, its team, a breakdown manager or the task creator can change this task."
      subDepts={t.gag_id != null ? subDepts : []}
      assignableUsers={deptUsers}
      mode="inline"
      onSave={(patch) => apiPut(taskUrl(t), patch)}
      onDelete={() => apiDelete(taskUrl(t))}
      onDirtyChange={setEditorDirty}
      saveRequestRef={editorSaveRef}
      discardRequestRef={editorDiscardRef}
      onSaved={() => fetchData({ silent: true })}
      onDeleted={() => { setExpandedId(null); setEditorDirty(false); fetchData({ silent: true }); }}
    />
  );

  const contextOf = (t) => {
    if (t.gag_id != null) {
      return { text: t.gag_title || `Gag ${t.gag_id}`, onClick: () => openGag(t.gag_id), title: `Open "${t.gag_title}" on the Breakdown` };
    }
    if (activeTab === 'given') return { text: 'No gag' };
    return { text: t.assigned_by != null ? `From ${t.assigned_by_name || 'someone'}` : 'Personal' };
  };

  const groupActions = (g) => {
    if (g.kind !== 'gag' || !g.gag) return null;
    const rel = g.gag.relation;
    const mayAdd = activeTab === 'mine' && (rel === 'owner' || rel === 'team' || canAssignOthers);
    // 📱 Phone (fix round, owner 2026-10-02): the header is TWO lines at most —
    // name, then counts — so `+ Add task` / `Open gag` move into the header's
    // own ⋯ (a bottom sheet below `md`), a 44px target beside the toggle.
    if (isMobile) {
      return (
        <Dropdown
          align="right"
          haspopup="menu"
          ariaLabel={`Actions for gag ${g.label}`}
          triggerTestId={`my-tasks-gag-menu-${g.gag.id}`}
          triggerClassName={`px-3 text-lg leading-none text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 ${TAP_TARGET_PHONE}`}
          label={<span aria-hidden="true">⋯</span>} hideChevron
          panelClassName="w-56 p-1"
        >
          {({ close }) => (
            <div role="menu" aria-label={`Actions for gag ${g.label}`}>
              {mayAdd && (
                <button type="button" role="menuitem" className={MENU_ITEM_NEUTRAL}
                  onClick={() => { close(); openNewTask({ gagId: g.gag.id }); }}
                  data-testid={`my-tasks-add-task-${g.gag.id}`}>
                  + Add task
                </button>
              )}
              <button type="button" role="menuitem" className={MENU_ITEM_NEUTRAL}
                onClick={() => { close(); openGag(g.gag.id); }}
                data-testid={`my-tasks-open-gag-${g.gag.id}`}>
                Open gag
              </button>
            </div>
          )}
        </Dropdown>
      );
    }
    return (
      <span className="flex items-center shrink-0">
        {mayAdd && (
          <button type="button" onClick={() => openNewTask({ gagId: g.gag.id })}
            className={`px-3 text-xs font-semibold text-indigo-700 dark:text-indigo-300 hover:underline ${TAP_TARGET_PHONE}`}
            data-testid={`my-tasks-add-task-${g.gag.id}`}>
            + Add task
          </button>
        )}
        <button type="button" onClick={() => openGag(g.gag.id)}
          title={`Open gag "${g.label}" on the Breakdown`}
          className={`px-3 text-xs font-semibold text-indigo-700 dark:text-indigo-300 hover:underline ${TAP_TARGET_PHONE}`}
          data-testid={`my-tasks-open-gag-${g.gag.id}`}>
          Open gag
        </button>
      </span>
    );
  };
  const decoratedGroups = groups.map((g) => ({
    ...g,
    summary: g.label != null ? <GroupSummary group={g} me={me} todayStr={todayStr} tab={activeTab} /> : null,
    actions: groupActions(g),
  }));

  // ── Kanban / Gantt (desktop) — the same rows, routed to the right endpoint.
  const changeTaskStatus = async (task, status) => {
    const url = task.gag_id != null ? `/api/gags/${task.gag_id}/tasks/${task.id}/status` : `/api/tasks/${task.id}/status`;
    const res = await apiPatch(url, { status });
    if (!res.ok) throw new Error('status change failed');
    fetchData({ silent: true });
  };
  const rescheduleTask = async (task, start, due) => {
    try {
      const res = await apiPut(taskUrl(task), { start_date: start, due_date: due });
      if (!res.ok) await apiThrow(res);
      fetchData({ silent: true });
    } catch (err) {
      toast.error(err);
      throw err;
    }
  };
  const ganttTasks = [];
  decoratedGroups.forEach((g, i) => {
    for (const t of g.tasks) ganttTasks.push({ ...t, _category: g.label || 'Tasks', _categoryOrder: i });
  });

  const totalOverdue = mineRows.filter((t) => isMine(t, me) && t.due_date && t.due_date < todayStr && t.status !== 'done' && t.status !== 'cancelled').length;
  usePageTitle('My Tasks');
  usePageSummary(data ? `· ${mineRows.filter((t) => isMine(t, me)).length} for you${totalOverdue ? ` · ${totalOverdue} overdue` : ''}` : '');

  if (loading) return <LoadingSpinner message="Loading your tasks..." />;
  if (error) return <ErrorMessage error={error} onRetry={() => fetchData()} />;

  // Under Group by Gag the group header already names the gag (or "From …" /
  // "Personal tasks"), so the per-row "Gag / From" column — and the gag name on
  // a phone row — is redundant there (owner, fix round 2026-10-02). Every other
  // grouping keeps it.
  const byGag = groupBy === 'gag';
  const columns = taskTableColumns({ hide: byGag ? ['context'] : [], contextLabel: activeTab === 'given' ? 'Gag' : 'Gag / From' });
  // View by ▾ — the shared `ui/GroupControl`, renamed by the owner. Phone reads
  // "View: Gag", desktop "View by · Gag" (the value muted, as Group ▾ did).
  // 📱 Unassigned (phone only): the SAME control over `UNASSIGNED_VIEW_OPTIONS`.
  const viewValue = isGagTab ? unassignedView : groupBy;
  const viewByControl = (!isGagTab || isMobile) ? (
    <GroupControl
      options={isGagTab ? UNASSIGNED_VIEW_OPTIONS : viewByOptions(isMobile)}
      value={viewValue}
      onChange={changeGroup}
      flatId="none"
      heading="View by"
      closeOnSelect
      shrinkable={isMobile}
      ariaLabel="View by"
      title={isGagTab ? 'View the unassigned gags by sub-department or shoot date, or as one list' : 'View the list by gag, date, status or person'}
      testId="my-tasks-view"
      renderLabel={(current) => (isMobile ? (
        // 📏 375px: beside an active status pill only ~60px of text fits, so
        // the phone uses the SHORT value and, while a status is active, drops
        // the visible "View:" (it stays for assistive tech). Measured.
        <span>
          <span className={statusTab !== TASK_STATUS_TAB_ALL ? 'sr-only' : undefined}>View: </span>
          {PHONE_VIEW_LABEL[viewValue] || current}
        </span>
      ) : (
        <span className="inline-flex items-center gap-1 whitespace-nowrap">
          View by
          <span className="text-gray-400 dark:text-gray-500 font-normal">· {current}</span>
        </span>
      ))}
    />
  ) : null;
  const statusChip = !isGagTab && statusShown ? (
    <FilterChipMenu
      options={statusOptions}
      value={statusTab}
      onChange={changeStatus}
      allId={TASK_STATUS_TAB_ALL}
      noun="Status"
      testId="my-tasks-status"
    />
  ) : null;
  // 🖥️ Desktop `Actions ▾` (fix round, owner 2026-10-02): Expand/Collapse all
  // and Open Breakdown moved off the bar into one menu, like the Breakdown's,
  // so the toolbar holds two rows at 1280 with the sidebar open. `+ New task`
  // stays the page's one filled control, outside the menu.
  const anyGroupClosed = titled.some((g) => !groupState.isOpen(g));
  const canExpandAll = !isGagTab && effectiveView === 'list' && titled.length > 1;
  const setAllGroups = (open) => {
    // Collapsing the group that holds the dirty editor would hide it — ask.
    if (!open && groups.some(holdsExpanded)) { ask('collapseAll'); return; }
    groupState.setAll(groups, open);
  };
  latest.current.collapseAll = () => groupState.setAll(groups, false);
  const actionsMenu = (
    <Dropdown
      align="right"
      haspopup="menu"
      panelClassName="w-56 p-1"
      triggerClassName={TOOLBAR_DISCLOSURE_TRIGGER}
      title="Page actions"
      ariaLabel="Page actions"
      triggerTestId="my-tasks-actions-menu"
      label="Actions"
    >
      {({ close }) => (
        <div role="menu" aria-label="Page actions">
          {canExpandAll && (
            <button type="button" role="menuitem" className={MENU_ITEM_NEUTRAL}
              onClick={() => { close(); setAllGroups(anyGroupClosed); }}
              data-testid="my-tasks-expand-all">
              {anyGroupClosed ? 'Expand all' : 'Collapse all'}
            </button>
          )}
          <Link to="/teams/breakdown" role="menuitem" className={MENU_ITEM_NEUTRAL}
            onClick={(e) => {
              close();
              // Dirty: hold the link and ask; the navigate runs once clean.
              if (editorDirty) { e.preventDefault(); ask('navigate', '/teams/breakdown'); }
            }}
            data-testid="my-tasks-open-breakdown">
            Open Breakdown
          </Link>
        </div>
      )}
    </Dropdown>
  );
  const newTaskButton = !isGagTab ? (
    <button type="button" onClick={() => openNewTask({})} className={BTN.primary} data-testid="my-tasks-new-task">
      + New task
    </button>
  ) : null;

  // 📱 Row 2's trailing control: a plain "+" (owner, 2026-10-02 — the ⋯ sheet
  // is gone: Expand all is not needed on a phone and Open Breakdown is in the
  // main menu; then "doesnt need a background. just a + will do"). No fill —
  // a 44px tap area around a blue glyph (blue-600 on white passes 3:1 for a
  // non-text control), the same New task flow as desktop's "+ New task".
  const phoneNewTask = !isGagTab ? (
    <PhoneAddButton label="New task" testId="my-tasks-new-task-phone" onClick={() => openNewTask({})} />
  ) : null;

  const body = () => {
    if (isGagTab) return <UnassignedList gags={unassigned} viewBy={unassignedView} isMobile={isMobile} stickyTop={headerTop} term={term} onOpenGag={openGag} onTaken={() => fetchData({ silent: true })} />;
    if (tabRows.length === 0) {
      return activeTab === 'mine'
        ? <EmptyState size="sm" title="Nothing on your plate here" description="Gags you own or work on, and tasks given to you, show up here." />
        : <EmptyState size="sm" title="You haven't delegated any tasks yet" />;
    }
    if (term && searched.length === 0) {
      return <EmptyState size="sm" title={`No tasks match “${term}”`} description="Try a different search, or clear it to see every task." testId="my-tasks-search-empty" />;
    }
    if (effectiveView === 'kanban') {
      return <TaskKanbanView tasks={visible} onTaskClick={openInTable} canDrag={canEditTask} onStatusChange={changeTaskStatus} />;
    }
    if (decoratedGroups.every((g) => g.tasks.length === 0 && !g.emptyNote)) {
      return <EmptyState size="sm" title="No tasks with this status" description="Pick another status, or clear the status filter to see the rest." />;
    }
    if (effectiveView === 'gantt') {
      return (
        /* One page scroll, band pinned under the toolbar (the Crew Gantt's
           layout) — this is the chart's only PAGE-level caller; desktop only. */
        <CrossTeamGanttView pageScroll hideSort stickyTop={headerTop} tasks={ganttTasks} onTaskClick={openInTable}
          categoryField="_category" categoryOrderField="_categoryOrder"
          canDrag={ganttTasks.some(canEditTask)}
          onDragComplete={async (task, s, d) => { if (canEditTask(task)) await rescheduleTask(task, s, d); }} />
      );
    }
    return (
      <TaskTable
        groups={decoratedGroups}
        groupState={groupState}
        columns={columns}
        isMobile={isMobile}
        stickyTop={headerTop}
        sort={groupBy === 'none' ? { field: sort.field, dir: sort.dir, onSort: (f) => setSort((s) => nextTaskSort(s, f)) } : null}
        expandedId={expandedId}
        onToggle={toggleRow}
        renderExpanded={renderExpanded}
        isFaded={(t) => fadeOthers && !isMine(t, me)}
        contextOf={byGag ? () => null : contextOf}
        rowMenu={(t) => [
          { label: 'Open', onSelect: () => { if (expandedId !== t.id) toggleRow(t); } },
          ...(canEditTask(t) && canAssignTask(t) ? [{ label: 'Assign', onSelect: () => { if (expandedId !== t.id) toggleRow(t); } }] : []),
          ...(t.gag_id != null ? [{ label: 'Open gag', onSelect: () => openGag(t.gag_id) }] : []),
        ]}
        editingRowId={editorDirty ? expandedId : null}
        testIdPrefix="my-task"
      />
    );
  };

  return (
    <div className={LIST_PAGE_SHELL}>
      {leaveGuard.dialog}
      {/* The three-way prompt over a dirty inline task edit — the order pages'
          and GagGroupTable's deck verbatim. */}
      <ConfirmDialog
        title="Unsaved changes to this task"
        message={`“${(allTasks.find((x) => x.id === expandedId) || {}).title || 'This task'}” has edits that have not been saved.`}
        {...UNSAVED_PROMPT_BUTTONS}
        variant="save"
        {...promptProps}
      />
      {/* The pinned toolbar. Phone: the shared header
          (`components/MobileTabSearchRow`): tabs, then 🔍 · View ▾ · +.
          Desktop: the Breakdown's shape — search · view tabs · chips, with
          Actions ▾ and + New task on the right, then the page tabs on a
          row of their own INSIDE the bar. */}
      <Toolbar
        ref={toolbarRef}
        marginClassName={isMobile ? 'mb-0.5' : 'mb-2'}
        data-testid="my-tasks-toolbar"
        // 📏 The CONTROLS are one row at 1280 with the sidebar open (owner,
        // header round); the page tabs wrap beneath them (`basis-full`). The
        // bar's ~966px could not hold six controls at their natural widths
        // (measured 972px at REST, ~1,130px with "● In progress ×" and
        // "View by · Sub-department"), and `flex-wrap` places items by their
        // UNSHRUNK size. From `xl` the row does not wrap; the search box is
        // the one item allowed to give way (`SEARCH_BOX_WIDTH` "may shrink,
        // never grow"); the chips and the right cluster are `shrink-0`.
        // 📱 Phone: the shared header's 2px padding, edge to edge.
        className={isMobile ? PHONE_HEADER_TOOLBAR : undefined}
      >
        {isMobile ? (
          <MobileTabSearchRow
            idPrefix="my-tasks"
            searchTestIdPrefix="my-tasks"
            searchText={search}
            onSearchChange={changeSearch}
            searchPlaceholder="Search tasks, gags, people…"
            searchLabel="Search tasks"
            searchAriaLabel="Search tasks"
            tabStrip={(
              <Tabs bare tabs={tabs} active={activeTab} onChange={changeTab} idPrefix="my-tasks-top" ariaLabel="My Tasks lists"
                scrollHints compact className="flex-1 min-w-0" />
            )}
            view={viewByControl}
            add={phoneNewTask}
          />
        ) : (
          <>
            <div className="basis-full min-w-0 flex flex-wrap xl:flex-nowrap items-center gap-x-4 gap-y-2" data-testid="my-tasks-controls">
            <SearchInput value={search} onChange={changeSearch} placeholder="Search tasks, gags, people…"
              ariaLabel="Search tasks" debounceMs={0} testId="my-tasks-search" />
            {/* Table | Kanban | Timeline — `ui/Tabs` on the search box's line,
                exactly as the Breakdown's Table | Timeline. Not on Unassigned,
                which is a list of gags. */}
            {!isGagTab && (
              <Tabs bare className="shrink-0" tabs={MY_TASKS_VIEW_TABS} active={view} onChange={changeView}
                idPrefix="my-tasks-view" ariaLabel="Task view" />
            )}
            {!isGagTab && (
              <div className="shrink-0 flex items-center gap-2" data-testid="my-tasks-chips">
                {statusChip}
                {effectiveView !== 'kanban' && viewByControl}
              </div>
            )}
            <div className={`${TOOLBAR_RIGHT_SLOT} shrink-0`} data-testid="my-tasks-toolbar-right">
              {actionsMenu}
              {newTaskButton}
            </div>
            </div>
            {/* The page tabs — their own row inside the sticky bar, the
                Breakdown's status-strip device (`min-w-0 overflow-x-auto` keeps
                a narrow bar from widening the page). */}
            <div className="basis-full min-w-0 overflow-x-auto overflow-y-hidden" data-testid="my-tasks-tab-strip">
              <Tabs bare tabs={tabs} active={activeTab} onChange={changeTab} idPrefix="my-tasks-top" ariaLabel="My Tasks lists" />
            </div>
          </>
        )}
      </Toolbar>

      <div className="pt-1" data-testid="my-tasks-body">
        {body()}
      </div>

      {newTask && (
        <NewTaskDialog
          gags={gags.filter((g) => g.relation === 'owner' || g.relation === 'team' || canAssignOthers)}
          presetGagId={newTask.gagId || null}
          me={me}
          canAssignOthers={canAssignOthers}
          deptUsers={deptUsers}
          gagById={gagById}
          onClose={() => setNewTask(null)}
          onCreated={() => { setNewTask(null); fetchData({ silent: true }); }}
        />
      )}
    </div>
  );
}

/**
 * + New task. A task needs a title; the gag is OPTIONAL (no gag = a no-gag
 * task, visible to its creator and assignees only). Assigning someone else
 * is offered only where the payload says the caller may (rule 3 on a gag: its
 * owner or breakdown rights; rule 5 without one: breakdown rights) — the
 * server refuses anything else.
 */
function NewTaskDialog({ gags, presetGagId, me, canAssignOthers, deptUsers, gagById, onClose, onCreated }) {
  const toast = useToast();
  const [title, setTitle] = useState('');
  const [due, setDue] = useState('');
  const [gagId, setGagId] = useState(presetGagId ? String(presetGagId) : '');
  const [assignees, setAssignees] = useState(me != null ? [Number(me)] : []);
  const [error, setError] = useState(null);
  const { guard, busy } = useSubmitGuard();

  const gag = gagId ? gagById.get(Number(gagId)) : null;
  const mayAssign = gag ? (canAssignOthers || gag.relation === 'owner') : true;
  const mayAssignOthers = gag ? mayAssign : canAssignOthers;
  const options = mayAssignOthers ? deptUsers : deptUsers.filter((u) => Number(u.id) === Number(me));

  const submit = () => guard(async () => {
    if (!title.trim()) { setError('Give the task a title.'); return; }
    setError(null);
    const body = { title: title.trim(), ...(due ? { due_date: due } : {}) };
    if (mayAssign && assignees.length > 0) body.assignee_ids = assignees;
    const res = await apiPost(gag ? `/api/gags/${gag.id}/tasks` : '/api/tasks', body);
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      setError(data.error || `Could not create the task (HTTP ${res.status})`);
      return;
    }
    toast.success('Task created');
    onCreated();
  });

  const nameOf = (uid) => (deptUsers.find((u) => Number(u.id) === Number(uid)) || {}).display_name || `User ${uid}`;
  return (
    <ConfirmDialog
      open
      title="New task"
      confirmLabel={busy ? 'Creating…' : 'Create task'}
      variant="save"
      busy={busy}
      confirmDisabled={!title.trim()}
      onConfirm={submit}
      onCancel={onClose}
    >
      <div className="space-y-3 text-left" data-testid="my-tasks-new-task-dialog">
        {error && <div className="px-3 py-2 text-sm rounded-lg bg-red-50 text-red-700 border border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800" role="alert">{error}</div>}
        <label className="block">
          <span className={FIELD_LABEL_CLASS}>Title</span>
          <input type="text" value={title} maxLength={200} onChange={(e) => setTitle(e.target.value)} autoFocus
            className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200" />
        </label>
        <label className="block">
          <span className={FIELD_LABEL_CLASS}>Due date</span>
          <input type="date" value={due} onChange={(e) => setDue(e.target.value)}
            className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200" />
        </label>
        <label className="block">
          <span className={FIELD_LABEL_CLASS}>Gag (optional)</span>
          <select value={gagId} onChange={(e) => setGagId(e.target.value)}
            className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200">
            <option value="">No gag — only you and the people you give it to will see it</option>
            {gags.map((g) => <option key={g.id} value={g.id}>{g.title}</option>)}
          </select>
        </label>
        <div>
          <span className={FIELD_LABEL_CLASS}>Assigned to</span>
          <div className="flex flex-wrap gap-1.5 mb-1.5">
            {assignees.map((uid) => (
              <span key={uid} className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full bg-blue-50 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200 text-xs">
                {Number(uid) === Number(me) ? 'Me' : nameOf(uid)}
                <button type="button" aria-label={`Unassign ${nameOf(uid)}`} onClick={() => setAssignees((a) => a.filter((x) => x !== uid))}
                  className="w-5 h-5 inline-flex items-center justify-center rounded-full hover:bg-blue-100 dark:hover:bg-blue-800/50"><span aria-hidden="true">✕</span></button>
              </span>
            ))}
            {assignees.length === 0 && <span className="text-xs text-gray-500 dark:text-gray-400">Nobody</span>}
          </div>
          {mayAssign ? (
            <UserAutocomplete value="" options={options} excludeUserIds={assignees} clearOnSelect ariaLabel="Assign someone"
              placeholder={mayAssignOthers ? 'Assign someone…' : 'Assign yourself…'}
              onChange={(_n, uid) => { if (uid != null) setAssignees((a) => (a.includes(uid) ? a : [...a, uid])); }} />
          ) : (
            <p className="text-xs text-gray-500 dark:text-gray-400">Only the gag owner or a breakdown manager can assign this gag&rsquo;s tasks.</p>
          )}
        </div>
      </div>
    </ConfirmDialog>
  );
}

const UNASSIGNED_COLUMNS = [
  { key: 'gag', label: 'Gag', width: 280, min: 160 },
  { key: 'shoot', label: 'Shoot', width: 120, min: 100 },
  { key: 'status', label: 'Status', width: 140, min: 120 },
  { key: 'tasks', label: 'Tasks', width: 90, min: 80 },
  { key: 'action', label: '', ariaLabel: 'Action', width: 180, min: 160 },
];

/**
 * Unassigned — unowned gags, grouped by `viewBy` (`groupUnassignedGags`;
 * desktop always Sub-department, a phone picks in View ▾). Only breakdown-rights
 * holders ever receive rows (the server sends `[]` to everyone else), and Take
 * is the existing `POST /api/gags/:gagId/take` (rule 6). Assigning someone
 * else happens on the Breakdown, where the owner picker lives.
 */
function UnassignedList({ gags, viewBy = 'subdept', isMobile, stickyTop, term, onOpenGag, onTaken }) {
  const toast = useToast();
  const { guard } = useSubmitGuard();
  const rows = term ? gags.filter((g) => String(g.title || '').toLowerCase().includes(term.toLowerCase())) : gags;
  const groups = groupUnassignedGags(rows, viewBy);
  const state = useGroupedList({ defaultOpen: true });
  const take = (g) => guard(async () => {
    try {
      const res = await apiPost(`/api/gags/${g.id}/take`);
      if (res.status === 409) toast.info('Someone beat you to it — the list has been refreshed.');
      else if (!res.ok) await apiThrow(res);
      else toast.success(`You own "${g.title}"`);
    } catch (err) { toast.error(err); }
    onTaken();
  });
  if (rows.length === 0) return <EmptyState size="sm" title={term ? `No gags match “${term}”` : 'Every gag here has an owner'} />;
  const statusPill = (g) => (
    <span className={`inline-flex ${statusPillClass()} ${GAG_STATUS_COLORS[g.status] || GAG_STATUS_COLORS.todo}`}>
      {GAG_STATUS_LABELS[g.status] || g.status}
    </span>
  );
  const actions = (g) => (
    <span className="flex items-center gap-2 justify-end">
      <button type="button" onClick={() => take(g)} className={`${BTN_XS.primary} ${TAP_TARGET_PHONE}`} data-testid={`my-tasks-take-${g.id}`}>Take</button>
      <button type="button" onClick={() => onOpenGag(g.id)} className={`text-xs font-semibold text-indigo-700 dark:text-indigo-300 hover:underline ${TAP_TARGET_PHONE}`}>Open gag</button>
    </span>
  );
  if (isMobile) {
    return (
      <div className="space-y-2" data-testid="my-tasks-unassigned">
        {groups.map((grp) => (
          <div key={grp.key} className={PHONE_GROUP_WRAPPER || undefined}>
            {grp.label != null && (
              <GroupHeader className={PHONE_GROUP_HEADER} open={state.isOpen(grp)} onToggle={() => state.toggle(grp)} label={grp.label}
                summary={<span className="text-xs text-gray-600 dark:text-gray-300">{grp.gags.length} {grp.gags.length === 1 ? 'gag' : 'gags'}</span>} />
            )}
            {(grp.label == null || state.isOpen(grp)) && (
              <div className={PHONE_SECTION_BODY} data-testid={`my-tasks-unassigned-group-${grp.key}`}>
                {grp.gags.map((g) => (
                  <div key={g.id} className={`${PHONE_FLOAT_ITEM} pl-2 pr-[5px] py-[5px] flex flex-col gap-1`}>
                    <span className="flex items-start gap-2">
                      <span className="flex-1 min-w-0 text-sm font-medium text-gray-800 dark:text-gray-200 break-words">{g.title}</span>
                      {statusPill(g)}
                    </span>
                    <span className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
                      <span className="flex-1">{g.shoot_date ? formatDateCompact(g.shoot_date) : 'No shoot date'} · {g.task_total} tasks</span>
                      {actions(g)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  }
  return (
    <PageScrollTable testIdPrefix="my-tasks-unassigned" columns={UNASSIGNED_COLUMNS} stickyTop={stickyTop}>
      {groups.flatMap((grp) => [
        <tr key={`h-${grp.key}`}>
          <td colSpan={UNASSIGNED_COLUMNS.length} className="p-0 border-t border-gray-200 dark:border-gray-700">
            <GroupHeader open={state.isOpen(grp)} onToggle={() => state.toggle(grp)} label={grp.label}
              summary={<span className="text-xs text-gray-600 dark:text-gray-300">{grp.gags.length} {grp.gags.length === 1 ? 'gag' : 'gags'}</span>} />
          </td>
        </tr>,
        ...(state.isOpen(grp) ? grp.gags.map((g) => (
          <tr key={g.id} data-testid={`my-tasks-unassigned-row-${g.id}`}>
            <td className="px-2 py-2 text-sm font-medium text-gray-800 dark:text-gray-200 break-words">{g.title}</td>
            <td className="px-2 py-2 text-sm text-gray-600 dark:text-gray-300">{g.shoot_date ? formatDateCompact(g.shoot_date) : <span className="text-gray-400 dark:text-gray-500">—</span>}</td>
            <td className="px-2 py-2">{statusPill(g)}</td>
            <td className="px-2 py-2 text-sm text-gray-600 dark:text-gray-300 tabular-nums">{g.task_total}</td>
            <td className="px-2 py-2">{actions(g)}</td>
          </tr>
        )) : []),
      ])}
    </PageScrollTable>
  );
}

